/**
 * React binding for the theme store.
 *
 * The store is headless; this provider wires it into the component tree,
 * starts the system watchers once per mount, and exposes `useTheme()`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type JSX,
  type ReactNode,
} from 'react';

import {
  applyResolvedTheme,
  getPrefs,
  newBgImageId,
  resolveMode,
  resolveMotion,
  subscribe,
  updatePrefs,
  watchSystem,
} from './theme-store.js';
import type {
  AccentKey,
  AutoMode,
  BgImageEntry,
  BgImageFit,
  ContrastLevel,
  MotionPref,
  ThemeMode,
  ThemePrefs,
} from './theme-types.js';

export interface ThemeContextValue {
  prefs: ThemePrefs;
  resolvedMode: ThemeMode;
  resolvedMotion: 'full' | 'reduced';
  setMode: (mode: ThemeMode) => void;
  setContrast: (contrast: ContrastLevel) => void;
  toggleContrast: () => void;
  setAccent: (accent: AccentKey) => void;
  setAutoMode: (auto: AutoMode) => void;
  setMotion: (motion: MotionPref) => void;
  setGradientHeight: (height: number) => void;
  /** Add a new image to the library and make it active. Returns the new id. */
  addBgImage: (dataUrl: string, name: string) => string;
  /** Remove an image from the library. If it was active, clears the selection. */
  removeBgImage: (id: string) => void;
  /** Select which image is applied as background. `null` = gradient. */
  setActiveBgImage: (id: string | null) => void;
  setBgImageFit: (fit: BgImageFit) => void;
  setBgTileSize: (sizeVw: number) => void;
  /** Switch to the custom accent slot and set its hex. */
  setCustomAccent: (hex: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  useEffect(() => {
    applyResolvedTheme();
    return watchSystem();
  }, []);

  const prefs = useSyncExternalStore(subscribe, getPrefs, getPrefs);

  const setMode = useCallback((mode: ThemeMode) => {
    // Explicit click on Light/Dark implies the user wants that specific
    // mode — flip autoMode to manual so system preference can't override.
    updatePrefs({ mode, autoMode: 'manual' });
  }, []);

  const setContrast = useCallback((contrast: ContrastLevel) => {
    updatePrefs({ contrast });
  }, []);

  const toggleContrast = useCallback(() => {
    updatePrefs({
      contrast: getPrefs().contrast === 'high' ? 'normal' : 'high',
    });
  }, []);

  const setAccent = useCallback((accent: AccentKey) => {
    updatePrefs({ accent });
  }, []);

  const setAutoMode = useCallback((auto: AutoMode) => {
    if (auto === 'system') {
      // Move the stored mode in sync with what the OS says right now so
      // the next toggle back to manual lands on a sensible value.
      const next: ThemeMode =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
      updatePrefs({ autoMode: auto, mode: next });
    } else {
      updatePrefs({ autoMode: auto });
    }
  }, []);

  const setMotion = useCallback((motion: MotionPref) => {
    updatePrefs({ motion });
  }, []);

  const setGradientHeight = useCallback((gradientHeight: number) => {
    updatePrefs({ gradientHeight });
  }, []);

  const addBgImage = useCallback((dataUrl: string, name: string): string => {
    const entry: BgImageEntry = {
      id: newBgImageId(),
      dataUrl,
      name,
      createdAt: Date.now(),
    };
    const prev = getPrefs();
    updatePrefs({
      bgImages: [entry, ...prev.bgImages],
      activeBgImageId: entry.id,
    });
    return entry.id;
  }, []);

  const removeBgImage = useCallback((id: string) => {
    const prev = getPrefs();
    const nextImages = prev.bgImages.filter((b) => b.id !== id);
    const nextActive =
      prev.activeBgImageId === id ? null : prev.activeBgImageId;
    updatePrefs({ bgImages: nextImages, activeBgImageId: nextActive });
  }, []);

  const setActiveBgImage = useCallback((activeBgImageId: string | null) => {
    updatePrefs({ activeBgImageId });
  }, []);

  const setBgImageFit = useCallback((bgImageFit: BgImageFit) => {
    updatePrefs({ bgImageFit });
  }, []);

  const setBgTileSize = useCallback((bgTileSize: number) => {
    updatePrefs({ bgTileSize });
  }, []);

  const setCustomAccent = useCallback((hex: string) => {
    updatePrefs({ accent: 'custom', customAccentHex: hex });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      prefs,
      resolvedMode: resolveMode(prefs),
      resolvedMotion: resolveMotion(prefs),
      setMode,
      setContrast,
      toggleContrast,
      setAccent,
      setAutoMode,
      setMotion,
      setGradientHeight,
      addBgImage,
      removeBgImage,
      setActiveBgImage,
      setBgImageFit,
      setBgTileSize,
      setCustomAccent,
    }),
    [
      prefs,
      setMode,
      setContrast,
      toggleContrast,
      setAccent,
      setAutoMode,
      setMotion,
      setGradientHeight,
      addBgImage,
      removeBgImage,
      setActiveBgImage,
      setBgImageFit,
      setBgTileSize,
      setCustomAccent,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
